// src/payload-guard.ts
var MAX_REPORT_PAYLOAD_BYTES = 4 * 1024 * 1024;
var MAX_SCREENSHOT_DATA_URL_BYTES = 1.5 * 1024 * 1024;
function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}
function estimateJsonBytes(value) {
  const json = safeStringify(value);
  return json === null ? Number.MAX_SAFE_INTEGER : new TextEncoder().encode(json).length;
}
function checkReportPayloadSize(payload, maxBytes = MAX_REPORT_PAYLOAD_BYTES) {
  const json = safeStringify(payload);
  if (json === null) {
    return {
      ok: false,
      bytes: 0,
      maxBytes,
      serializeFailed: true,
      reason: "Report could not be serialized (circular reference in metadata?)"
    };
  }
  const bytes = new TextEncoder().encode(json).length;
  if (bytes <= maxBytes) {
    return { ok: true, bytes, maxBytes };
  }
  return {
    ok: false,
    bytes,
    maxBytes,
    reason: `Report payload ${formatBytes(bytes)} exceeds limit ${formatBytes(maxBytes)}`
  };
}
function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// src/digest.ts
function getSubtle() {
  try {
    const g = globalThis;
    const crypto2 = g["crypto"];
    if (crypto2?.subtle) return crypto2.subtle;
  } catch {
  }
  return null;
}
function bytesToHex(bytes) {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function sha256Hex(value) {
  const subtle = getSubtle();
  if (subtle) {
    const buffer = await subtle.digest("SHA-256", new TextEncoder().encode(value));
    return bytesToHex(new Uint8Array(buffer));
  }
  const { sha256 } = await import('@noble/hashes/sha2.js');
  return bytesToHex(sha256(new TextEncoder().encode(value)));
}
async function hmacSha256Hex(secret, value) {
  const subtle = getSubtle();
  if (subtle) {
    const key = await subtle.importKey(
      "raw",
      new TextEncoder().encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );
    const buffer = await subtle.sign("HMAC", key, new TextEncoder().encode(value));
    return bytesToHex(new Uint8Array(buffer));
  }
  const { hmac } = await import('@noble/hashes/hmac.js');
  const { sha256 } = await import('@noble/hashes/sha2.js');
  const result = hmac(sha256, new TextEncoder().encode(secret), new TextEncoder().encode(value));
  return bytesToHex(result);
}

// src/api-client.ts
var _credWarningEmitted = false;
var MUSHI_USER_TOKEN_HEADER = "X-Mushi-User-Token";
var MUSHI_PROJECT_HEADER = "X-Mushi-Project";
var MUSHI_SDK_PACKAGE_HEADER = "X-Mushi-SDK-Package";
var MUSHI_SDK_VERSION_HEADER = "X-Mushi-SDK-Version";
function buildSdkIngestHeaders(opts) {
  const headers = {
    "Content-Type": "application/json",
    "X-Mushi-Api-Key": opts.apiKey,
    [MUSHI_PROJECT_HEADER]: opts.projectId
  };
  if (opts.sdkPackage) headers[MUSHI_SDK_PACKAGE_HEADER] = opts.sdkPackage;
  if (opts.sdkVersion) headers[MUSHI_SDK_VERSION_HEADER] = opts.sdkVersion;
  if (opts.userToken) headers[MUSHI_USER_TOKEN_HEADER] = opts.userToken;
  if (opts.internalKind) headers[MUSHI_INTERNAL_HEADER] = opts.internalKind;
  return headers;
}
var DEFAULT_API_ENDPOINT = "https://dxptnwrhwsqckaftyymj.supabase.co/functions/v1/api";
var MUSHI_INTERNAL_HEADER = "X-Mushi-Internal";
var MUSHI_INTERNAL_INIT_MARKER = "__mushiInternal";
var DEFAULT_TIMEOUT = 1e4;
var DEFAULT_MAX_RETRIES = 2;
var DEFAULT_CIRCUIT_THRESHOLD = 4;
var DEFAULT_CIRCUIT_COOLDOWN_MS = 3e4;
function createApiClient(options) {
  const {
    projectId,
    apiKey,
    apiEndpoint = DEFAULT_API_ENDPOINT,
    timeout = DEFAULT_TIMEOUT,
    maxRetries = DEFAULT_MAX_RETRIES,
    getUserToken,
    sdkPackage,
    sdkVersion,
    circuitBreaker
  } = options;
  let baseUrl = apiEndpoint.replace(/\/$/, "");
  const cbEnabled = circuitBreaker?.enabled !== false;
  const cbThreshold = Math.max(1, circuitBreaker?.threshold ?? DEFAULT_CIRCUIT_THRESHOLD);
  const cbCooldownMs = Math.max(1e3, circuitBreaker?.cooldownMs ?? DEFAULT_CIRCUIT_COOLDOWN_MS);
  let cbFailures = 0;
  let cbOpenUntil = 0;
  const cbIsOpen = () => cbEnabled && cbOpenUntil > Date.now();
  const cbRecordReachable = () => {
    cbFailures = 0;
    cbOpenUntil = 0;
  };
  const cbRecordUnreachable = () => {
    if (!cbEnabled) return;
    cbFailures += 1;
    if (cbFailures >= cbThreshold) cbOpenUntil = Date.now() + cbCooldownMs;
  };
  async function request(method, path, body, retries = maxRetries, internalKind, extraHeaders) {
    if (cbIsOpen()) {
      return { ok: false, error: { code: "CIRCUIT_OPEN", message: "Endpoint temporarily unavailable; retrying later." } };
    }
    const url = `${baseUrl}${path}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    const userToken = getUserToken?.() ?? null;
    try {
      const response = await fetch(url, {
        method,
        headers: {
          ...buildSdkIngestHeaders({
            apiKey,
            projectId,
            sdkPackage,
            sdkVersion,
            userToken,
            internalKind
          }),
          ...extraHeaders
        },
        body: body ? JSON.stringify(body) : void 0,
        signal: controller.signal,
        ...internalKind ? { [MUSHI_INTERNAL_INIT_MARKER]: internalKind } : {}
      });
      clearTimeout(timer);
      if (response.status === 307 || response.status === 308) {
        const target = response.headers.get("Location");
        if (target && retries > 0) {
          const targetBase = target.replace(/\/v1\/.*$/, "").replace(/\/$/, "");
          if (targetBase !== baseUrl) {
            baseUrl = targetBase;
            return request(method, path, body, retries - 1, internalKind, extraHeaders);
          }
        }
      }
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        if ((response.status === 401 || response.status === 403) && !_credWarningEmitted) {
          _credWarningEmitted = true;
          const where = baseUrl === DEFAULT_API_ENDPOINT ? "Get the correct values at: https://kensaur.us/mushi-mushi/admin/projects" : "Get the correct values from your Mushi console's Projects page.";
          console.error(
            `[Mushi] Credentials rejected (HTTP ${response.status}). Check your Project ID and API key scope (must be "report:write"). ` + where
          );
        }
        if (response.status >= 500 && retries > 0) {
          await sleep(getBackoffDelay(maxRetries - retries));
          return request(method, path, body, retries - 1, internalKind, extraHeaders);
        }
        if (response.status >= 500) cbRecordUnreachable();
        else cbRecordReachable();
        return {
          ok: false,
          error: {
            code: errorBody.error?.code || `HTTP_${response.status}`,
            message: errorBody.error?.message || errorBody.message || `HTTP ${response.status} error`
          }
        };
      }
      const payload = await response.json();
      const data = payload && typeof payload === "object" && "ok" in payload && "data" in payload ? payload.data : payload;
      cbRecordReachable();
      return { ok: true, data };
    } catch (error) {
      clearTimeout(timer);
      if (retries > 0 && isRetryable(error)) {
        await sleep(getBackoffDelay(maxRetries - retries));
        return request(method, path, body, retries - 1, internalKind);
      }
      cbRecordUnreachable();
      return {
        ok: false,
        error: {
          code: "NETWORK_ERROR",
          message: error instanceof Error ? error.message : "Unknown network error"
        }
      };
    }
  }
  async function requestForReporter(method, path, reporterToken, body) {
    const tokenHash = await sha256Hex(reporterToken);
    const ts = String(Date.now());
    const hmac = await hmacSha256Hex(apiKey, `${projectId}.${ts}.${tokenHash}`);
    const url = `${baseUrl}${path}`;
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-Mushi-Api-Key": apiKey,
        "X-Mushi-Project": projectId,
        ...sdkPackage ? { [MUSHI_SDK_PACKAGE_HEADER]: sdkPackage } : {},
        ...sdkVersion ? { [MUSHI_SDK_VERSION_HEADER]: sdkVersion } : {},
        [MUSHI_INTERNAL_HEADER]: "reporter-poll",
        "X-Reporter-Token-Hash": tokenHash,
        "X-Reporter-Ts": ts,
        "X-Reporter-Hmac": hmac
      },
      body: body ? JSON.stringify(body) : void 0,
      [MUSHI_INTERNAL_INIT_MARKER]: "reporter-poll"
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: {
          code: `HTTP_${response.status}`,
          message: errorBody.error?.message ?? errorBody.message ?? `HTTP ${response.status} error`
        }
      };
    }
    const payload = await response.json();
    return { ok: true, data: payload.data ?? payload };
  }
  return {
    async submitReport(report) {
      const guard = checkReportPayloadSize(report);
      if (!guard.ok) {
        return {
          ok: false,
          error: {
            code: guard.serializeFailed ? "SERIALIZE_FAILED" : "PAYLOAD_TOO_LARGE",
            message: guard.reason ?? "Report payload exceeds size limit"
          }
        };
      }
      return request("POST", "/v1/reports", report, maxRetries, "report-submit");
    },
    async getReportStatus(reportId) {
      return request("GET", `/v1/reports/${reportId}/status`, void 0, maxRetries, "report-status");
    },
    async getSdkConfig() {
      return request("GET", "/v1/sdk/config", void 0, maxRetries, "sdk-config");
    },
    async askAssistant(input) {
      return request(
        "POST",
        "/v1/sdk/assistant",
        {
          message: input.message,
          threadId: input.threadId ?? null,
          context: input.context ?? null
        },
        1,
        "community"
      );
    },
    async getLatestSdkVersion(packageName) {
      const query = new URLSearchParams({ package: packageName }).toString();
      return request("GET", `/v1/sdk/latest-version?${query}`, void 0, maxRetries, "sdk-config");
    },
    async postDiscoveryEvent(event) {
      return request(
        "POST",
        "/v1/sdk/discovery",
        event,
        1,
        "discovery"
      );
    },
    async listReporterReports(reporterToken) {
      return requestForReporter("GET", "/v1/reporter/reports", reporterToken);
    },
    async listReporterComments(reportId, reporterToken) {
      return requestForReporter(
        "GET",
        `/v1/reporter/reports/${reportId}/comments`,
        reporterToken
      );
    },
    async replyToReporterReport(reportId, reporterToken, body, feedbackSignal) {
      return requestForReporter(
        "POST",
        `/v1/reporter/reports/${reportId}/reply`,
        reporterToken,
        { body, ...feedbackSignal ? { feedback_signal: feedbackSignal } : {} }
      );
    },
    async reopenReporterReport(reportId, reporterToken, note) {
      return requestForReporter(
        "POST",
        `/v1/reporter/reports/${reportId}/reopen`,
        reporterToken,
        { note: note ?? "" }
      );
    },
    async listNotifications(reporterToken, opts) {
      const qs = new URLSearchParams();
      if (opts?.since) qs.set("since", opts.since);
      if (opts?.limit) qs.set("limit", String(opts.limit));
      const suffix = qs.size ? `?${qs.toString()}` : "";
      return requestForReporter(
        "GET",
        `/v1/notifications${suffix}`,
        reporterToken
      );
    },
    async markNotificationRead(notificationId, reporterToken) {
      return requestForReporter(
        "POST",
        `/v1/notifications/${notificationId}/read`,
        reporterToken,
        {}
      );
    },
    async listReporterFeatureBoard(reporterToken) {
      return requestForReporter(
        "GET",
        "/v1/reporter/feature-board",
        reporterToken
      );
    },
    async voteReporterFeatureBoard(requestId, reporterToken) {
      return requestForReporter(
        "POST",
        `/v1/reporter/feature-board/${requestId}/vote`,
        reporterToken,
        {}
      );
    },
    // ─── Rewards program (P1) ──────────────────────────────────
    async submitActivity(userId, events, opts) {
      return request(
        "POST",
        "/v1/sdk/activity",
        {
          user_id: userId,
          user_traits: opts?.userTraits,
          opted_in: opts?.optedIn,
          reporter_token_hash: opts?.reporterTokenHash,
          // P2: JWT for monetary verification; omitted when null
          ...opts?.hostJwt ? { host_jwt: opts.hostJwt } : {},
          events
        },
        1,
        // best-effort, 1 retry
        "discovery"
      );
    },
    async getMyPoints(userId) {
      return request(
        "GET",
        `/v1/sdk/me/points?userId=${encodeURIComponent(userId)}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    async getMyTier(userId) {
      return request(
        "GET",
        `/v1/sdk/me/tier?userId=${encodeURIComponent(userId)}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    async getMyHistory(userId, opts) {
      const qs = new URLSearchParams({ userId, ...opts?.limit ? { limit: String(opts.limit) } : {} });
      return request(
        "GET",
        `/v1/sdk/me/history?${qs}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    async getHallOfFame(limit = 10) {
      return request(
        "GET",
        `/v1/sdk/hall-of-fame?limit=${limit}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    // ─── Cross-app community (in-widget tester identity) ──────────
    async sendMagicLink(email) {
      return request(
        "POST",
        "/v1/tester/magic-link",
        { email },
        1,
        "community"
      );
    },
    async linkReporterToken(reporterTokenHash, jwt) {
      return request(
        "POST",
        "/v1/tester/link-reporter",
        { reporter_token_hash: reporterTokenHash },
        1,
        "community",
        { Authorization: `Bearer ${jwt}` }
      );
    },
    async getCrossAppReports(jwt, opts) {
      const qs = new URLSearchParams();
      if (opts?.limit) qs.set("limit", String(opts.limit));
      if (opts?.offset) qs.set("offset", String(opts.offset));
      const suffix = qs.size ? `?${qs.toString()}` : "";
      return request(
        "GET",
        `/v1/tester/cross-app-reports${suffix}`,
        void 0,
        1,
        "community",
        { Authorization: `Bearer ${jwt}` }
      );
    },
    async getMyReputation(jwt) {
      return request(
        "GET",
        "/v1/tester/reputation",
        void 0,
        1,
        "community",
        { Authorization: `Bearer ${jwt}` }
      );
    },
    async getPublicLeaderboard(limit = 20) {
      return request(
        "GET",
        `/v1/public/tester-leaderboard?limit=${limit}`,
        void 0,
        1,
        "reporter-poll"
      );
    },
    async getTesterStatus(jwt) {
      return request(
        "GET",
        "/v1/me/tester-status",
        void 0,
        1,
        "community",
        { Authorization: `Bearer ${jwt}` }
      );
    }
  };
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function getBackoffDelay(attempt) {
  return Math.min(1e3 * 2 ** attempt + Math.random() * 500, 1e4);
}
function isRetryable(error) {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  if (error instanceof TypeError) return true;
  return false;
}

// src/region.ts
var REGION_ENDPOINTS = {
  us: "https://dxptnwrhwsqckaftyymj.supabase.co/functions/v1/api",
  eu: "https://dxptnwrhwsqckaftyymj.supabase.co/functions/v1/api",
  jp: "https://dxptnwrhwsqckaftyymj.supabase.co/functions/v1/api"
};
var ROUTING_CACHE_KEY = "mushi_region_v1";
var ROUTING_CACHE_TTL_MS = 24 * 60 * 60 * 1e3;
async function resolveRegionEndpoint(opts) {
  const explicit = opts.region;
  if (explicit && explicit !== "self" && REGION_ENDPOINTS[explicit]) {
    return REGION_ENDPOINTS[explicit];
  }
  const storage = opts.storage ?? safeLocalStorage();
  const cached = readCache(storage, opts.projectId);
  if (cached) return cached;
  try {
    const fetcher = opts.fetcher ?? fetch;
    const url = `${opts.apiEndpoint.replace(/\/$/, "")}/v1/region/resolve?project_id=${encodeURIComponent(opts.projectId)}`;
    const res = await fetcher(url, { method: "GET" });
    if (!res.ok) return opts.apiEndpoint;
    const body = await res.json();
    if (!body.region || !body.endpoint) return opts.apiEndpoint;
    writeCache(storage, opts.projectId, { region: body.region, endpoint: body.endpoint, ts: Date.now() });
    return body.endpoint;
  } catch {
    return opts.apiEndpoint;
  }
}
function safeLocalStorage() {
  try {
    return typeof globalThis !== "undefined" && "localStorage" in globalThis ? globalThis.localStorage : void 0;
  } catch {
    return void 0;
  }
}
function readCache(storage, projectId) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(`${ROUTING_CACHE_KEY}:${projectId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > ROUTING_CACHE_TTL_MS) return null;
    return parsed.endpoint;
  } catch {
    return null;
  }
}
function writeCache(storage, projectId, entry) {
  if (!storage) return;
  try {
    storage.setItem(`${ROUTING_CACHE_KEY}:${projectId}`, JSON.stringify(entry));
  } catch {
  }
}

// src/env-config.ts
function first(...values) {
  return values.find((v) => v !== void 0 && v !== "");
}
function tryImportMetaEnv(key) {
  try {
    const env = import.meta.env;
    return typeof env === "object" && env !== null ? env[key] : void 0;
  } catch {
    return void 0;
  }
}
function readProcessEnv(read) {
  try {
    return read();
  } catch {
    return void 0;
  }
}
function resolveEnvConfig() {
  const projectId = first(
    tryImportMetaEnv("VITE_MUSHI_PROJECT_ID"),
    tryImportMetaEnv("NEXT_PUBLIC_MUSHI_PROJECT_ID"),
    tryImportMetaEnv("NUXT_PUBLIC_MUSHI_PROJECT_ID"),
    tryImportMetaEnv("EXPO_PUBLIC_MUSHI_PROJECT_ID"),
    readProcessEnv(() => process.env.NEXT_PUBLIC_MUSHI_PROJECT_ID),
    readProcessEnv(() => process.env.EXPO_PUBLIC_MUSHI_PROJECT_ID),
    readProcessEnv(() => process.env.MUSHI_PROJECT_ID)
  );
  const apiKey = first(
    tryImportMetaEnv("VITE_MUSHI_API_KEY"),
    tryImportMetaEnv("NEXT_PUBLIC_MUSHI_API_KEY"),
    tryImportMetaEnv("NUXT_PUBLIC_MUSHI_API_KEY"),
    tryImportMetaEnv("EXPO_PUBLIC_MUSHI_API_KEY"),
    readProcessEnv(() => process.env.NEXT_PUBLIC_MUSHI_API_KEY),
    readProcessEnv(() => process.env.EXPO_PUBLIC_MUSHI_API_KEY),
    readProcessEnv(() => process.env.MUSHI_API_KEY)
  );
  const apiEndpoint = first(
    tryImportMetaEnv("VITE_MUSHI_API_ENDPOINT"),
    tryImportMetaEnv("NEXT_PUBLIC_MUSHI_API_ENDPOINT"),
    tryImportMetaEnv("NUXT_PUBLIC_MUSHI_API_ENDPOINT"),
    tryImportMetaEnv("EXPO_PUBLIC_MUSHI_API_ENDPOINT"),
    readProcessEnv(() => process.env.NEXT_PUBLIC_MUSHI_API_ENDPOINT),
    readProcessEnv(() => process.env.EXPO_PUBLIC_MUSHI_API_ENDPOINT),
    readProcessEnv(() => process.env.MUSHI_API_ENDPOINT)
  );
  const result = {};
  if (projectId) result.projectId = projectId;
  if (apiKey) result.apiKey = apiKey;
  if (apiEndpoint) result.apiEndpoint = apiEndpoint;
  return result;
}

// src/pre-filter.ts
var DEFAULT_MIN_LENGTH = 10;
var DEFAULT_MAX_LENGTH = 2e3;
var SPAM_PATTERNS = [
  /^(.)\1{10,}$/,
  // repeated single character
  /^[A-Z\s!?]{20,}$/,
  // all caps shouting
  /^[\d\s]+$/,
  // numbers only
  /^[^a-zA-Z\u00C0-\u024F\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF]{10,}$/,
  // no real letters
  // "test" removed — users legitimately write "I was testing…" in real reports.
  // Keep clearly-nonsense patterns that are never real sentences.
  /^(asdf|qwerty|lorem ipsum)[.,!?\s]*$/i
  // standalone keyboard-mash or placeholder
];
var GIBBERISH_PATTERN = /^[bcdfghjklmnpqrstvwxz]{6,}/i;
function createPreFilter(config = {}) {
  const {
    enabled = true,
    blockObviousSpam = true,
    minDescriptionLength = DEFAULT_MIN_LENGTH,
    maxDescriptionLength = DEFAULT_MAX_LENGTH
  } = config;
  function check(description) {
    if (!enabled) {
      return { passed: true };
    }
    const trimmed = description.trim();
    if (trimmed.length < minDescriptionLength) {
      return { passed: false, reason: `Too short (min ${minDescriptionLength} characters)` };
    }
    if (trimmed.length > maxDescriptionLength) {
      return { passed: false, reason: `Too long (max ${maxDescriptionLength} characters)` };
    }
    if (blockObviousSpam) {
      for (const pattern of SPAM_PATTERNS) {
        if (pattern.test(trimmed)) {
          return { passed: false, reason: "Detected as spam" };
        }
      }
      if (GIBBERISH_PATTERN.test(trimmed)) {
        return { passed: false, reason: "Detected as gibberish" };
      }
      const words = trimmed.split(/\s+/).filter((w) => w.length > 1);
      if (words.length < 2) {
        return { passed: false, reason: "Description needs at least 2 words" };
      }
    }
    return { passed: true };
  }
  function truncate(description) {
    const trimmed = description.trim();
    if (trimmed.length <= maxDescriptionLength) return trimmed;
    return trimmed.slice(0, maxDescriptionLength) + "...";
  }
  return { check, truncate };
}

// src/logger.ts
var LEVEL_VALUE = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  fatal: 50,
  silent: 99
};
var LEVEL_LABEL = {
  debug: "DBG",
  info: "INF",
  warn: "WRN",
  error: "ERR",
  fatal: "FTL"
};
var ANSI = {
  reset: "\x1B[0m",
  dim: "\x1B[2m",
  bold: "\x1B[1m",
  cyan: "\x1B[36m",
  green: "\x1B[32m",
  yellow: "\x1B[33m",
  red: "\x1B[31m",
  white: "\x1B[37m",
  bgRed: "\x1B[41m"
};
var LEVEL_COLOR = {
  debug: ANSI.dim,
  info: ANSI.green,
  warn: ANSI.yellow,
  error: ANSI.red,
  fatal: `${ANSI.bgRed}${ANSI.white}${ANSI.bold}`
};
function detectFormat() {
  try {
    if (typeof globalThis.Deno !== "undefined") return "json";
  } catch {
  }
  const proc = typeof globalThis.process !== "undefined" ? globalThis.process : void 0;
  if (proc?.env) {
    if (proc.env.NODE_ENV === "production") return "json";
    if (proc.env.LOG_FORMAT === "json") return "json";
    if (proc.env.LOG_FORMAT === "pretty") return "pretty";
    if (proc.stdout?.isTTY) return "pretty";
  }
  if (typeof globalThis.window !== "undefined") return "pretty";
  return "json";
}
function flattenMeta(meta) {
  const parts = [];
  for (const [k, v] of Object.entries(meta)) {
    if (v === void 0 || v === null) continue;
    if (typeof v === "object") {
      parts.push(`${k}=${JSON.stringify(v)}`);
    } else {
      parts.push(`${k}=${String(v)}`);
    }
  }
  return parts.join(" ");
}
function formatPretty(entry) {
  const { ts, level, scope, msg, ...rest } = entry;
  const time = ts.slice(11, 23);
  const color = LEVEL_COLOR[level] ?? "";
  const label = LEVEL_LABEL[level] ?? level.toUpperCase();
  const metaStr = Object.keys(rest).length > 0 ? ` ${ANSI.dim}${flattenMeta(rest)}${ANSI.reset}` : "";
  return `${ANSI.dim}${time}${ANSI.reset} ${color}${label}${ANSI.reset} ${ANSI.cyan}[${scope}]${ANSI.reset} ${msg}${metaStr}`;
}
function formatJson(entry) {
  return JSON.stringify(entry);
}
function makeEmitter(destination) {
  if (destination === "stderr") {
    return (level, formatted) => {
      console.error(formatted);
    };
  }
  return (level, formatted) => {
    switch (level) {
      case "error":
      case "fatal":
        console.error(formatted);
        break;
      case "warn":
        console.warn(formatted);
        break;
      default:
        console.log(formatted);
    }
  };
}
function buildLogger(scope, minLevel, baseMeta, formatter, emit) {
  let currentLevel = minLevel;
  function log(level, msg, meta) {
    if (LEVEL_VALUE[level] < LEVEL_VALUE[currentLevel]) return;
    const entry = {
      ts: (/* @__PURE__ */ new Date()).toISOString(),
      level,
      scope,
      msg,
      ...baseMeta,
      ...meta
    };
    emit(level, formatter(entry));
  }
  return {
    debug: (msg, meta) => log("debug", msg, meta),
    info: (msg, meta) => log("info", msg, meta),
    warn: (msg, meta) => log("warn", msg, meta),
    error: (msg, meta) => log("error", msg, meta),
    fatal: (msg, meta) => log("fatal", msg, meta),
    child(childScope, childMeta) {
      return buildLogger(
        `${scope}:${childScope}`,
        currentLevel,
        { ...baseMeta, ...childMeta },
        formatter,
        emit
      );
    },
    setLevel(level) {
      currentLevel = level;
    }
  };
}
function createLogger(options) {
  const {
    scope,
    level = "info",
    meta = {},
    format = "auto",
    destination = "stdout"
  } = options;
  const resolvedFormat = format === "auto" ? detectFormat() : format;
  const formatter = resolvedFormat === "json" ? formatJson : formatPretty;
  const emit = makeEmitter(destination);
  return buildLogger(scope, level, meta, formatter, emit);
}
var noopLogger = {
  debug: () => {
  },
  info: () => {
  },
  warn: () => {
  },
  error: () => {
  },
  fatal: () => {
  },
  child: () => noopLogger,
  setLevel: () => {
  }
};

// src/queue-crypto.ts
var KEY_DB = "mushi-mushi-keyring";
var KEY_STORE = "keys";
var KEY_RECORD_ID = "offline-queue/v1";
var cachedKey = null;
var cachedKeyPromise = null;
function hasWebCrypto() {
  return typeof globalThis !== "undefined" && typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.subtle !== "undefined" && typeof indexedDB !== "undefined";
}
function openKeyDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(KEY_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(KEY_STORE)) {
        db.createObjectStore(KEY_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function loadKey() {
  const db = await openKeyDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, "readonly");
    const req = tx.objectStore(KEY_STORE).get(KEY_RECORD_ID);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}
async function storeKey(key) {
  const db = await openKeyDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, "readwrite");
    tx.objectStore(KEY_STORE).put(key, KEY_RECORD_ID);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function getOfflineQueueKey() {
  if (cachedKey) return cachedKey;
  if (cachedKeyPromise) return cachedKeyPromise;
  if (!hasWebCrypto()) {
    throw new Error("Web Crypto + IndexedDB required for offline queue encryption");
  }
  cachedKeyPromise = (async () => {
    const existing = await loadKey();
    if (existing) {
      cachedKey = existing;
      return existing;
    }
    const key = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );
    await storeKey(key);
    cachedKey = key;
    return key;
  })();
  return cachedKeyPromise;
}
function bytesToB64(bytes) {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
function b64ToBytes(s) {
  const bin = atob(s);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
async function encryptJson(plain) {
  const key = await getOfflineQueueKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(plain));
  const cipher = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data));
  return { _mme: 1, iv: bytesToB64(iv), ct: bytesToB64(cipher) };
}
function isEncryptedPayload(v) {
  return !!v && typeof v === "object" && v._mme === 1 && typeof v.iv === "string" && typeof v.ct === "string";
}
async function decryptJson(payload) {
  const key = await getOfflineQueueKey();
  const iv = b64ToBytes(payload.iv);
  const ct = b64ToBytes(payload.ct);
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(plain));
}

// src/queue.ts
var queueLog = createLogger({ scope: "mushi:queue", level: "warn" });
var DB_NAME = "mushi-mushi";
var STORE_NAME = "offline-reports";
var DB_VERSION = 1;
var LS_KEY = "mushi_offline_queue";
var BATCH_SIZE = 10;
var MAX_BACKOFF_MS = 6e4;
var AUTO_FLUSH_INTERVAL_MS = 3e4;
var MAX_DELIVERY_ATTEMPTS = 8;
var MAX_QUEUE_AGE_MS = 24 * 60 * 60 * 1e3;
function createOfflineQueue(config = {}) {
  const { enabled = true, maxQueueSize = 50, syncOnReconnect = true, encryptAtRest = true } = config;
  let syncCleanup = null;
  let flushInterval = null;
  let backendType = null;
  async function wrapForStorage(report) {
    const queuedAt = (/* @__PURE__ */ new Date()).toISOString();
    if (!encryptAtRest) {
      return { ...report, queuedAt };
    }
    try {
      const payload = await encryptJson(report);
      return { id: report.id, queuedAt, payload };
    } catch (err) {
      queueLog.warn("Offline queue: encryption failed, storing plaintext", { err: String(err) });
      return { ...report, queuedAt };
    }
  }
  async function unwrapForSend(row) {
    if (isEncryptedRecord(row)) {
      try {
        return await decryptJson(row.payload);
      } catch (err) {
        queueLog.warn("Offline queue: decrypt failed, dropping row", { err: String(err), id: row.id });
        return null;
      }
    }
    return row;
  }
  function isEncryptedRecord(row) {
    return !!row.payload && isEncryptedPayload(row.payload);
  }
  function rowAttempts(row) {
    const n = row.attempts;
    return typeof n === "number" && Number.isFinite(n) ? n : 0;
  }
  function isExpired(row, now) {
    const queuedAt = row.queuedAt;
    if (!queuedAt) return false;
    const ts = Date.parse(queuedAt);
    if (Number.isNaN(ts)) return false;
    return now - ts > MAX_QUEUE_AGE_MS;
  }
  function detectBackend() {
    if (backendType) return backendType;
    if (typeof indexedDB !== "undefined") {
      backendType = "indexeddb";
    } else if (typeof localStorage !== "undefined") {
      backendType = "localstorage";
    } else {
      backendType = "none";
    }
    return backendType;
  }
  function openDb() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        backendType = "localstorage";
        reject(request.error);
      };
    });
  }
  async function idbEnqueue(report) {
    const db = await openDb();
    const row = await wrapForStorage(report);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(row);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  async function idbGetAll() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  async function idbDelete(id) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  async function idbPutRow(row) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(row);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  async function idbSize() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  async function idbClear() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  function lsRead() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }
  function lsWrite(rows) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(rows));
    } catch {
    }
  }
  async function lsEnqueue(report) {
    const rows = lsRead();
    rows.push(await wrapForStorage(report));
    lsWrite(rows);
  }
  function lsDelete(id) {
    const rows = lsRead().filter((r) => r.id !== id);
    lsWrite(rows);
  }
  function lsUpdateRow(row) {
    const rows = lsRead();
    const idx = rows.findIndex((r) => r.id === row.id);
    if (idx >= 0) {
      rows[idx] = row;
      lsWrite(rows);
    }
  }
  async function removeRow(backend, rowId) {
    try {
      if (backend === "indexeddb") await idbDelete(rowId);
      else lsDelete(rowId);
    } catch (err) {
      queueLog.debug("Offline queue: row removal failed (will age out)", {
        id: rowId,
        err: String(err)
      });
    }
  }
  async function persistRow(backend, row) {
    try {
      if (backend === "indexeddb") await idbPutRow(row);
      else lsUpdateRow(row);
      return true;
    } catch (err) {
      queueLog.debug("Offline queue: row persist failed", {
        id: row.id,
        err: String(err)
      });
      return false;
    }
  }
  async function enqueue(report) {
    if (!enabled) return;
    const currentSize = await size();
    if (currentSize >= maxQueueSize) {
      queueLog.warn("Offline queue full \u2014 dropping report", { maxQueueSize });
      return;
    }
    const backend = detectBackend();
    if (backend === "indexeddb") {
      try {
        await idbEnqueue(report);
        return;
      } catch {
        backendType = "localstorage";
      }
    }
    if (backend === "localstorage" || backendType === "localstorage") {
      await lsEnqueue(report);
      return;
    }
  }
  function getBackoffDelay2(attempt) {
    return Math.min(1e3 * 2 ** attempt + Math.random() * 500, MAX_BACKOFF_MS);
  }
  function sleep2(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  async function flush(client) {
    if (!enabled) return { sent: 0, failed: 0 };
    let rows;
    const backend = detectBackend();
    if (backend === "indexeddb") {
      try {
        rows = await idbGetAll();
      } catch {
        rows = lsRead();
      }
    } else {
      rows = lsRead();
    }
    const now = Date.now();
    const fresh = [];
    for (const row of rows) {
      if (isExpired(row, now)) {
        const rowId = row.id;
        await removeRow(backend, rowId);
        queueLog.debug("Offline queue: evicting stale report", { id: rowId });
      } else {
        fresh.push(row);
      }
    }
    rows = fresh;
    const batch = rows.slice(0, BATCH_SIZE);
    let sent = 0;
    let failed = 0;
    for (let i = 0; i < batch.length; i++) {
      const row = batch[i];
      const rowId = row.id;
      const report = await unwrapForSend(row);
      if (!report) {
        await removeRow(backend, rowId);
        failed++;
        continue;
      }
      const result = await client.submitReport(report);
      if (result.ok) {
        await removeRow(backend, rowId);
        sent++;
      } else {
        const permanent = result.error?.code === "HTTP_400" || result.error?.code === "HTTP_413" || result.error?.code === "HTTP_422" || result.error?.code === "INGEST_ERROR" || result.error?.code === "VALIDATION_ERROR" || // A payload that exceeds the size guard will never shrink on its own;
        // retrying re-serialises the multi-MB body every sync tick and wedges
        // the queue (it matches neither permanent nor transient otherwise).
        // SERIALIZE_FAILED (circular ref) is likewise unrecoverable on retry.
        result.error?.code === "PAYLOAD_TOO_LARGE" || result.error?.code === "SERIALIZE_FAILED" || typeof result.error?.message === "string" && /invalid payload|description must be at least|validation/i.test(
          result.error.message
        );
        const transient = !permanent && (result.error?.code === "NETWORK_ERROR" || result.error?.code === "HTTP_403" || result.error?.code === "HTTP_429" || result.error?.code === "HTTP_502" || result.error?.code === "HTTP_503" || result.error?.code === "HTTP_504" || typeof result.error?.code === "string" && result.error.code.startsWith("HTTP_5"));
        if (permanent) {
          await removeRow(backend, rowId);
        } else if (transient) {
          const nextAttempts = rowAttempts(row) + 1;
          if (nextAttempts >= MAX_DELIVERY_ATTEMPTS) {
            await removeRow(backend, rowId);
            queueLog.warn("Offline queue: giving up on report after repeated failures", {
              id: rowId,
              attempts: nextAttempts,
              code: result.error?.code
            });
          } else {
            row.attempts = nextAttempts;
            const persisted = await persistRow(backend, row);
            if (persisted) {
              queueLog.debug("Offline queue: transient failure, will retry", {
                id: rowId,
                attempts: nextAttempts,
                code: result.error?.code
              });
            } else {
              await removeRow(backend, rowId);
              queueLog.warn("Offline queue: dropping report (attempt counter unpersistable)", {
                id: rowId,
                code: result.error?.code
              });
            }
          }
        }
        failed++;
        if (i < batch.length - 1) {
          await sleep2(getBackoffDelay2(i));
        }
      }
    }
    return { sent, failed };
  }
  async function size() {
    const backend = detectBackend();
    if (backend === "indexeddb") {
      try {
        return await idbSize();
      } catch {
        return lsRead().length;
      }
    }
    return lsRead().length;
  }
  async function clear() {
    const backend = detectBackend();
    if (backend === "indexeddb") {
      try {
        await idbClear();
      } catch {
      }
    }
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
    }
  }
  function startAutoSync(client) {
    if (!enabled || !syncOnReconnect || typeof window === "undefined") return;
    const tryFlush = () => {
      if (navigator.onLine) {
        flush(client).catch((err) => {
          queueLog.warn("Offline queue auto-flush failed", { err: String(err) });
        });
      }
    };
    window.addEventListener("online", tryFlush);
    flushInterval = setInterval(() => {
      void size().then((n) => {
        if (n > 0) tryFlush();
      });
    }, AUTO_FLUSH_INTERVAL_MS);
    syncCleanup = () => {
      window.removeEventListener("online", tryFlush);
      if (flushInterval) {
        clearInterval(flushInterval);
        flushInterval = null;
      }
    };
  }
  function stopAutoSync() {
    syncCleanup?.();
    syncCleanup = null;
  }
  return { enqueue, flush, size, clear, startAutoSync, stopAutoSync };
}

// src/environment.ts
function captureEnvironment() {
  const nav = typeof navigator !== "undefined" ? navigator : void 0;
  const win = typeof window !== "undefined" ? window : void 0;
  const doc = typeof document !== "undefined" ? document : void 0;
  const scr = typeof screen !== "undefined" ? screen : void 0;
  void kickOffUserAgentData(nav);
  const connection = nav && "connection" in nav ? nav.connection : void 0;
  const w = win;
  const isCapacitor = !!w?.Capacitor;
  const isCordova = !!w?.cordova;
  const isReactNative = nav?.product === "ReactNative" || typeof globalThis.HermesInternal !== "undefined";
  const native = isCapacitor || isCordova || isReactNative ? {
    ...isCapacitor ? { capacitor: true } : {},
    ...isCordova ? { cordova: true } : {},
    ...isReactNative ? { reactNative: true } : {}
  } : void 0;
  return {
    userAgent: nav?.userAgent ?? "unknown",
    platform: nav?.platform ?? "unknown",
    language: nav?.language ?? "en",
    viewport: {
      width: win?.innerWidth ?? 0,
      height: win?.innerHeight ?? 0
    },
    url: win?.location?.href ?? "",
    referrer: doc?.referrer ?? "",
    timestamp: (/* @__PURE__ */ new Date()).toISOString(),
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone ?? "UTC",
    connection: connection ? {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt
    } : void 0,
    deviceMemory: nav?.deviceMemory,
    hardwareConcurrency: nav?.hardwareConcurrency,
    route: win?.location?.pathname,
    nearestTestid: findNearestTestidFromActive(doc),
    userAgentData: captureUserAgentData(nav),
    screen: captureScreen(scr, win),
    prefersColorScheme: matchScheme(win),
    prefersReducedMotion: matchMedia(win, "(prefers-reduced-motion: reduce)"),
    prefersReducedData: matchMedia(win, "(prefers-reduced-data: reduce)"),
    prefersContrast: matchContrast(win),
    forcedColors: matchMedia(win, "(forced-colors: active)"),
    online: typeof nav?.onLine === "boolean" ? nav.onLine : void 0,
    displayMode: matchDisplayMode(win),
    documentTitle: doc?.title?.slice(0, 200),
    buildId: readBuildIdMeta(doc),
    pageLoadTiming: capturePageLoadTiming(win),
    ...native ? { native } : {}
  };
}
function findNearestTestidFromActive(doc) {
  if (!doc) return void 0;
  let cur = doc.activeElement ?? null;
  let hops = 0;
  while (cur && hops < 20) {
    const tid = cur.getAttribute?.("data-testid");
    if (tid) return tid;
    cur = cur.parentElement;
    hops++;
  }
  return void 0;
}
var cachedHighEntropy = null;
var highEntropyKickedOff = false;
function kickOffUserAgentData(nav) {
  if (highEntropyKickedOff) return;
  const ua = nav?.userAgentData;
  if (!ua?.getHighEntropyValues) return;
  highEntropyKickedOff = true;
  ua.getHighEntropyValues([
    "architecture",
    "bitness",
    "model",
    "platformVersion",
    "uaFullVersion",
    "fullVersionList"
  ]).then((v) => {
    cachedHighEntropy = v;
  }).catch(() => {
  });
}
function pickBrand(brands) {
  if (!brands?.length) return void 0;
  const real = brands.filter((b) => !/not.?a.?brand/i.test(b.brand));
  if (real.length === 0) return void 0;
  const named = real.find((b) => !/chromium|google chrome/i.test(b.brand));
  return named ?? real[0];
}
function captureUserAgentData(nav) {
  const low = nav?.userAgentData;
  if (!low && !cachedHighEntropy) return void 0;
  const fullList = cachedHighEntropy?.fullVersionList;
  const brand = pickBrand(fullList ?? low?.brands);
  const out = {};
  if (brand) {
    out.browser = brand.brand;
    out.browserVersion = brand.version;
  }
  if (low?.platform) out.os = low.platform;
  if (cachedHighEntropy?.platformVersion) out.osVersion = cachedHighEntropy.platformVersion;
  if (typeof low?.mobile === "boolean") out.mobile = low.mobile;
  if (cachedHighEntropy?.model) out.model = cachedHighEntropy.model;
  if (cachedHighEntropy?.architecture) out.architecture = cachedHighEntropy.architecture;
  if (cachedHighEntropy?.bitness) out.bitness = cachedHighEntropy.bitness;
  return Object.keys(out).length === 0 ? void 0 : out;
}
function captureScreen(scr, win) {
  if (!scr && !win) return void 0;
  const out = {};
  if (typeof scr?.width === "number") out.width = scr.width;
  if (typeof scr?.height === "number") out.height = scr.height;
  if (typeof win?.devicePixelRatio === "number") out.devicePixelRatio = win.devicePixelRatio;
  if (typeof scr?.colorDepth === "number") out.colorDepth = scr.colorDepth;
  const orientationType = scr?.orientation?.type;
  if (orientationType) out.orientation = orientationType;
  return Object.keys(out).length === 0 ? void 0 : out;
}
function matchMedia(win, query) {
  if (!win?.matchMedia) return void 0;
  try {
    return win.matchMedia(query).matches;
  } catch {
    return void 0;
  }
}
function matchScheme(win) {
  if (!win?.matchMedia) return void 0;
  if (matchMedia(win, "(prefers-color-scheme: dark)")) return "dark";
  if (matchMedia(win, "(prefers-color-scheme: light)")) return "light";
  return "no-preference";
}
function matchContrast(win) {
  if (!win?.matchMedia) return void 0;
  if (matchMedia(win, "(prefers-contrast: more)")) return "more";
  if (matchMedia(win, "(prefers-contrast: less)")) return "less";
  if (matchMedia(win, "(prefers-contrast: custom)")) return "custom";
  return "no-preference";
}
function matchDisplayMode(win) {
  if (!win?.matchMedia) return void 0;
  if (matchMedia(win, "(display-mode: fullscreen)")) return "fullscreen";
  if (matchMedia(win, "(display-mode: standalone)")) return "standalone";
  if (matchMedia(win, "(display-mode: minimal-ui)")) return "minimal-ui";
  if (matchMedia(win, "(display-mode: browser)")) return "browser";
  return void 0;
}
function readBuildIdMeta(doc) {
  if (!doc) return void 0;
  const el = doc.querySelector?.('meta[name="mushi:build"]');
  const v = el?.content?.trim();
  if (!v) return void 0;
  return v.slice(0, 64);
}
function capturePageLoadTiming(win) {
  const perf = win?.performance;
  if (!perf?.getEntriesByType) return void 0;
  let entry;
  try {
    const entries = perf.getEntriesByType("navigation");
    entry = entries[0];
  } catch {
    return void 0;
  }
  if (!entry) return void 0;
  const start = entry.startTime ?? 0;
  const out = {};
  if (entry.domContentLoadedEventEnd > 0)
    out.domContentLoadedMs = Math.round(entry.domContentLoadedEventEnd - start);
  if (entry.loadEventEnd > 0) out.loadCompleteMs = Math.round(entry.loadEventEnd - start);
  if (entry.responseStart > 0) out.timeToFirstByteMs = Math.round(entry.responseStart - start);
  if (typeof entry.type === "string") out.navigationType = entry.type;
  return Object.keys(out).length === 0 ? void 0 : out;
}

// src/reporter-token.ts
var LEGACY_STORAGE_KEY = "mushi_reporter_token";
function storageKey(projectId) {
  return `mushi:reporter-token:${projectId}`;
}
function generateToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `mushi_${crypto.randomUUID()}`;
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `mushi_${hex}`;
}
function readStoredToken(key) {
  if (typeof localStorage === "undefined") return null;
  try {
    const existing = localStorage.getItem(key);
    return existing && existing.length > 0 ? existing : null;
  } catch {
    return null;
  }
}
function writeStoredToken(key, token) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key, token);
  } catch {
  }
}
function getReporterToken(projectId) {
  if (!projectId) {
    const legacy2 = readStoredToken(LEGACY_STORAGE_KEY);
    if (legacy2) return legacy2;
    const token2 = generateToken();
    writeStoredToken(LEGACY_STORAGE_KEY, token2);
    return token2;
  }
  const key = storageKey(projectId);
  const scoped = readStoredToken(key);
  if (scoped) return scoped;
  const legacy = readStoredToken(LEGACY_STORAGE_KEY);
  const token = legacy ?? generateToken();
  writeStoredToken(key, token);
  return token;
}

// src/uuid.ts
function newUuid() {
  const c = typeof crypto !== "undefined" ? crypto : void 0;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = bytes[6] & 15 | 64;
  bytes[8] = bytes[8] & 63 | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

// src/fingerprint.ts
var CACHE_KEY = "mushi_fingerprint_hash";
function collectInputs() {
  const nav = typeof navigator !== "undefined" ? navigator : void 0;
  const scr = typeof screen !== "undefined" ? screen : void 0;
  const win = typeof window !== "undefined" ? window : void 0;
  return {
    userAgent: nav?.userAgent ?? "unknown",
    platform: nav?.platform ?? "unknown",
    language: nav?.language ?? "en",
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone ?? "UTC",
    screenWidth: scr?.width ?? 0,
    screenHeight: scr?.height ?? 0,
    pixelRatio: win?.devicePixelRatio ?? 1,
    deviceMemory: nav?.deviceMemory,
    hardwareConcurrency: nav?.hardwareConcurrency
  };
}
async function sha256Hex2(input) {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const buf = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return `fbk_${(hash >>> 0).toString(16).padStart(8, "0")}`;
}
async function getDeviceFingerprintHash() {
  if (typeof localStorage !== "undefined") {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return cached;
  }
  const inputs = collectInputs();
  const serialised = JSON.stringify(inputs);
  const hash = await sha256Hex2(serialised);
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(CACHE_KEY, hash);
    } catch {
    }
  }
  return hash;
}

// src/session.ts
var SESSION_KEY = "mushi_session_id";
var cachedSessionId = null;
function getSessionId() {
  if (cachedSessionId) return cachedSessionId;
  if (typeof sessionStorage !== "undefined") {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      cachedSessionId = existing;
      return existing;
    }
  }
  const id = generateSessionId();
  cachedSessionId = id;
  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.setItem(SESSION_KEY, id);
    } catch {
    }
  }
  return id;
}
function generateSessionId() {
  const timestamp = Date.now().toString(36);
  return `ms_${timestamp}_${randomSuffix()}`;
}
function randomSuffix() {
  const c = typeof crypto !== "undefined" ? crypto : void 0;
  const bytes = new Uint8Array(4);
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let n = 0;
  for (const b of bytes) n = n * 256 + b >>> 0;
  return n.toString(36).padStart(6, "0").slice(0, 6);
}

// src/rate-limiter.ts
var DEFAULT_MAX_BURST = 10;
var DEFAULT_REFILL_RATE = 1;
var DEFAULT_REFILL_INTERVAL_MS = 5e3;
function createRateLimiter(config = {}) {
  const {
    maxBurst = DEFAULT_MAX_BURST,
    refillRate = DEFAULT_REFILL_RATE,
    refillIntervalMs = DEFAULT_REFILL_INTERVAL_MS
  } = config;
  let tokens = maxBurst;
  let lastRefill = Date.now();
  function refill() {
    const now = Date.now();
    const elapsed = now - lastRefill;
    const refills = Math.floor(elapsed / refillIntervalMs);
    if (refills > 0) {
      tokens = Math.min(maxBurst, tokens + refills * refillRate);
      lastRefill = now;
    }
  }
  function tryConsume() {
    refill();
    if (tokens > 0) {
      tokens--;
      return true;
    }
    return false;
  }
  function reset() {
    tokens = maxBurst;
    lastRefill = Date.now();
  }
  function availableTokens() {
    refill();
    return tokens;
  }
  return { tryConsume, reset, availableTokens };
}

// src/pii-scrubber.ts
var ORDERED_PATTERNS = [
  { key: "ssns", regex: /\b\d{3}-\d{2}-\d{4}\b/g, replacement: "[REDACTED_SSN]" },
  { key: "creditCards", regex: /\b(?:\d[ -]*){12,18}\d\b/g, replacement: "[REDACTED_CC]" },
  // Vendor secret tokens — mirrors packages/server/.../pii-scrubber.ts exactly.
  { key: "secretTokens", regex: /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g, replacement: "[REDACTED_AWS_KEY]" },
  { key: "secretTokens", regex: /(?:aws_secret_access_key|secret_access_key)["'\s:=]+[A-Za-z0-9/+=]{40}\b/gi, replacement: "aws_secret_access_key=[REDACTED_AWS_SECRET]" },
  { key: "secretTokens", regex: /\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{24,}\b/g, replacement: "[REDACTED_STRIPE_KEY]" },
  { key: "secretTokens", regex: /\bpk_(?:live|test)_[A-Za-z0-9]{24,}\b/g, replacement: "[REDACTED_STRIPE_PK]" },
  { key: "secretTokens", regex: /\bxox[abpor]-[A-Za-z0-9-]{10,}\b/g, replacement: "[REDACTED_SLACK_TOKEN]" },
  { key: "secretTokens", regex: /\bghp_[A-Za-z0-9]{36}\b/g, replacement: "[REDACTED_GITHUB_PAT]" },
  { key: "secretTokens", regex: /\bgithub_pat_[A-Za-z0-9_]{80,}\b/g, replacement: "[REDACTED_GITHUB_PAT]" },
  { key: "secretTokens", regex: /\bsk-(?:proj-)?[A-Za-z0-9_-]{20,}\b/g, replacement: "[REDACTED_OPENAI_KEY]" },
  { key: "secretTokens", regex: /\bsk-ant-[A-Za-z0-9_-]{20,}\b/g, replacement: "[REDACTED_ANTHROPIC_KEY]" },
  { key: "secretTokens", regex: /\bAIza[0-9A-Za-z_-]{35}\b/g, replacement: "[REDACTED_GOOGLE_KEY]" },
  { key: "secretTokens", regex: /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, replacement: "[REDACTED_JWT]" },
  { key: "emails", regex: /\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b/g, replacement: "[REDACTED_EMAIL]" },
  { key: "phones", regex: /(?:\+\d{1,3}[\s.-])?\(?\d{2,4}\)?[\s.-]\d{3,4}[\s.-]\d{3,4}\b/g, replacement: "[REDACTED_PHONE]" },
  { key: "ipAddresses", regex: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g, replacement: "[REDACTED_IP]" },
  { key: "ipv6", regex: /\b(?:[A-Fa-f0-9]{1,4}:){2,7}[A-Fa-f0-9]{0,4}\b/g, replacement: "[REDACTED_IPV6]" }
];
var DEFAULT_CONFIG = {
  emails: true,
  phones: true,
  creditCards: true,
  ssns: true,
  ipAddresses: false,
  // Secret tokens default ON — if they leak into a bug report there's no
  // good reason to ship them to our servers. Cheaper to scrub client-side.
  secretTokens: true,
  ipv6: false
};
function createPiiScrubber(config = {}) {
  const merged = { ...DEFAULT_CONFIG, ...config };
  const activePatterns = ORDERED_PATTERNS.filter((p) => merged[p.key]);
  function scrub(text) {
    if (!text) return text;
    let result = text;
    for (const { regex, replacement } of activePatterns) {
      result = result.replace(new RegExp(regex.source, regex.flags), replacement);
    }
    return result;
  }
  function scrubObject(obj, keys) {
    const copy = { ...obj };
    for (const key of keys) {
      if (typeof copy[key] === "string") {
        copy[key] = scrub(copy[key]);
      }
    }
    return copy;
  }
  return { scrub, scrubObject };
}
function scrubPii(text, config) {
  return createPiiScrubber(config).scrub(text);
}

// src/breadcrumbs.ts
var DEFAULT_MAX = 50;
var DEFAULT_MAX_MESSAGE = 500;
function createBreadcrumbBuffer(options = {}) {
  const max = Math.max(1, options.max ?? DEFAULT_MAX);
  const maxMsg = Math.max(50, options.maxMessageLength ?? DEFAULT_MAX_MESSAGE);
  let entries = [];
  return {
    add(input) {
      const ts = typeof input.timestamp === "number" ? input.timestamp : Date.now();
      const message = typeof input.message === "string" && input.message.length > maxMsg ? `${input.message.slice(0, maxMsg)}\u2026` : input.message;
      const crumb = {
        timestamp: ts,
        category: input.category,
        level: input.level ?? "info",
        message: message ?? "",
        ...input.data ? { data: input.data } : {}
      };
      entries.push(crumb);
      while (entries.length > max) entries.shift();
    },
    getAll() {
      return entries.slice();
    },
    clear() {
      entries = [];
    },
    size() {
      return entries.length;
    }
  };
}

// src/exception-normaliser.ts
var STACK_LIMIT = 8 * 1024;
var FALLBACK_JSON_LIMIT = 1e3;
function normaliseThrown(thrown) {
  if (thrown instanceof Error) {
    const name = thrown.name || "Error";
    const message = thrown.message || String(thrown);
    const stack = typeof thrown.stack === "string" && thrown.stack.length > 0 ? thrown.stack.slice(0, STACK_LIMIT) : void 0;
    const cause = thrown.cause;
    return {
      name,
      message,
      ...stack ? { stack } : {},
      ...cause !== void 0 ? { cause: cause instanceof Error ? cause.message : cause } : {}
    };
  }
  if (typeof thrown === "string") {
    return { name: "Error", message: thrown };
  }
  if (thrown && typeof thrown === "object") {
    const obj = thrown;
    const name = typeof obj.name === "string" ? obj.name : "Error";
    const message = typeof obj.message === "string" ? obj.message : (() => {
      try {
        return JSON.stringify(obj).slice(0, FALLBACK_JSON_LIMIT);
      } catch {
        return String(obj);
      }
    })();
    const stack = typeof obj.stack === "string" ? obj.stack.slice(0, STACK_LIMIT) : void 0;
    return { name, message, ...stack ? { stack } : {} };
  }
  if (thrown === void 0) return { name: "Error", message: "unknown" };
  return { name: "Error", message: String(thrown) };
}

// src/design-tokens.ts
var MUSHI_COLORS_LIGHT = {
  paper: "#F8F4ED",
  paperRaised: "#FFFFFF",
  ink: "#0E0D0B",
  inkMuted: "#5C5852",
  inkFaint: "#9A9489",
  inkDim: "#6E6760",
  rule: "rgba(14,13,11,0.10)",
  ruleStrong: "rgba(14,13,11,0.16)",
  accent: "#E03C2C",
  accentWash: "rgba(224,60,44,0.08)",
  accentInk: "#7A1F15",
  ok: "#16A34A",
  danger: "#DC2626"
};
var MUSHI_COLORS_DARK = {
  paper: "#0F0E0C",
  paperRaised: "#1A1815",
  ink: "#F2EBDD",
  inkMuted: "#928B7E",
  inkFaint: "#5A5650",
  inkDim: "#7A7268",
  rule: "rgba(242,235,221,0.10)",
  ruleStrong: "rgba(242,235,221,0.18)",
  accent: "#FF5A47",
  accentWash: "rgba(255,90,71,0.12)",
  accentInk: "#FFE5E0",
  ok: "#4ADE80",
  danger: "#F87171"
};
var MUSHI_TIER_COLORS = {
  free: MUSHI_COLORS_LIGHT.inkMuted,
  explorer: "#2563EB",
  contributor: "#7C3AED",
  champion: "#D97706",
  default: MUSHI_COLORS_LIGHT.accent
};
var MUSHI_BANNER_NEON = {
  bg: "#0FFF50",
  fg: "#0a1a0a",
  border: "#00C43A"
};
var MUSHI_ON_ACCENT = "#FAF7F0";
var MUSHI_INVERSE = "#ffffff";
var MUSHI_ACCENT_SHADOW = {
  light: "#9A2A1E",
  dark: "#7A1F15"
};
var MUSHI_BANNER_BRAND_BORDER = {
  light: "#B52F1F",
  dark: "#C4321E"
};
var MUSHI_REPORTER_STATUS = {
  light: {
    sent: { fg: "#1E4A8C", bg: "rgba(30,74,140,0.08)", border: "rgba(30,74,140,0.16)" },
    review: { fg: "#8A5A00", bg: "rgba(180,120,0,0.10)", border: "rgba(180,120,0,0.18)" },
    fixing: { fg: "#9A3D12", bg: "rgba(224,60,44,0.10)", border: "rgba(224,60,44,0.18)" },
    fixed: { fg: "#1F6B3A", bg: "rgba(31,107,58,0.10)", border: "rgba(31,107,58,0.18)" }
  },
  dark: {
    sent: { fg: "#A8C4FF", bg: "rgba(120,160,255,0.12)", border: "rgba(120,160,255,0.22)" },
    review: { fg: "#FFD27A", bg: "rgba(255,190,90,0.12)", border: "rgba(255,190,90,0.22)" },
    fixing: { fg: "#FFB899", bg: "rgba(255,120,60,0.12)", border: "rgba(255,120,60,0.24)" },
    fixed: { fg: "#8FE3B0", bg: "rgba(80,200,130,0.12)", border: "rgba(80,200,130,0.22)" }
  }
};
function mushiPalette(mode) {
  return mode === "dark" ? MUSHI_COLORS_DARK : MUSHI_COLORS_LIGHT;
}
var MUSHI_SPACING = {
  hairline: 2,
  tight: 4,
  snug: 8,
  comfy: 12,
  roomy: 16,
  lounge: 20,
  open: 24,
  wide: 32
};
var MUSHI_RADIUS = {
  none: 0,
  control: 4,
  card: 10,
  sheet: 16,
  pill: 999
};
var MUSHI_TYPE = {
  fontDisplay: `'Iowan Old Style', 'Palatino Linotype', 'Palatino', 'Book Antiqua', 'Cambria', Georgia, 'Times New Roman', serif`,
  fontBody: `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI Variable Display', 'Segoe UI', sans-serif`,
  fontMono: `ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, Consolas, 'Liberation Mono', monospace`,
  sizeBody: 14,
  sizeLabel: 12,
  sizeTitle: 16,
  lineBody: 1.55
};
var MUSHI_Z = {
  base: 99999,
  /** banner sits one below the trigger so an open FAB overlaps it */
  banner: 99998,
  /** report/assistant panel sits one above the trigger */
  panel: 1e5,
  /** element-selector overlay — always on top */
  overlay: 2147483647
};
var MUSHI_MOTION = {
  easeStamp: "cubic-bezier(0.22, 1, 0.36, 1)",
  durationFast: 200,
  durationPanel: 300
};
var MUSHI_GEOMETRY = {
  bannerHeight: 36,
  fabSize: 52,
  /** edge-tab launcher width (px) */
  edgeTabWidth: 32,
  /** default gutter from the screen edge (added on top of safe-area insets) */
  gutter: 24,
  /** Web panel width (px) — matches shipped Shadow DOM widget */
  panelWidth: 360,
  /** max panel height before keyboard inset (px) */
  panelMaxHeight: 480,
  /** below this viewport width the panel becomes a full-width bottom sheet */
  panelSheetBreakpoint: 480
};
function safeWidgetHex(v) {
  return /^#[0-9a-fA-F]{3,8}$/.test(v) ? v : "";
}
function resolveWidgetAccent(mode, accent = "", accentText = "") {
  const pal = mushiPalette(mode);
  const safeAccent = safeWidgetHex(accent);
  const safeAccentText = safeWidgetHex(accentText);
  const resolvedAccent = safeAccent || pal.accent;
  const accentWash = safeAccent ? mode === "dark" ? `${resolvedAccent}1F` : `${resolvedAccent}14` : pal.accentWash;
  const accentInk = safeAccentText || pal.accentInk;
  return { accent: resolvedAccent, accentWash, accentInk };
}
var MUSHI_COPY = {
  bannerLabel: "Beta",
  bannerMessage: "Found a bug? Tap to report it.",
  bugCta: "Report a bug",
  featureCta: "Feature request",
  triggerText: "\u{1F41B}",
  panelTitle: "Send feedback",
  myReportsTab: "My reports",
  assistantTab: "Ask"
};
function mushiTokens(mode) {
  return {
    mode,
    colors: mushiPalette(mode),
    spacing: MUSHI_SPACING,
    radius: MUSHI_RADIUS,
    type: MUSHI_TYPE,
    z: MUSHI_Z,
    motion: MUSHI_MOTION,
    geometry: MUSHI_GEOMETRY,
    copy: MUSHI_COPY
  };
}

// src/identity.ts
var MUSHI_IDENTITY_TOKEN_PREFIX = "mushi.identity";
function buildIdentityClaims(input) {
  const now = Math.floor(Date.now() / 1e3);
  const ttl = input.ttlSeconds && input.ttlSeconds > 0 ? Math.min(input.ttlSeconds, 3600) : 600;
  return {
    projectId: input.projectId,
    sub: input.userId,
    ...input.email ? { email: input.email } : {},
    ...input.name ? { name: input.name } : {},
    iat: now,
    exp: now + ttl
  };
}
function base64UrlDecode(segment) {
  const pad = segment.length % 4 === 0 ? "" : "=".repeat(4 - segment.length % 4);
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/") + pad;
  if (typeof atob === "function") return atob(base64);
  const B = globalThis.Buffer;
  if (B) return B.from(base64, "base64").toString("binary");
  return "";
}
function parseIdentityToken(token) {
  if (typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    const payload = JSON.parse(json);
    const projectId = typeof payload.projectId === "string" ? payload.projectId : void 0;
    const sub = typeof payload.sub === "string" ? payload.sub : void 0;
    if (!projectId || !sub) return null;
    return {
      projectId,
      sub,
      ...typeof payload.email === "string" ? { email: payload.email } : {},
      ...typeof payload.name === "string" ? { name: payload.name } : {},
      ...typeof payload.iat === "number" ? { iat: payload.iat } : {},
      ...typeof payload.exp === "number" ? { exp: payload.exp } : {}
    };
  } catch {
    return null;
  }
}

// src/favicon.ts
var MULTI_TENANT_PARENT_HOSTS = /* @__PURE__ */ new Set(["kensaur.us", "www.kensaur.us"]);
var SLUG_DOMAIN_HINTS = {
  "glot-it": "glot.it",
  glotit: "glot.it",
  "solo-boss-cloud": "sbc-front.vercel.app",
  "mushi-mushi": "kensaur.us"
};
var SLUG_ICON_URL_HINTS = {
  "mushi-mushi": "https://kensaur.us/mushi-mushi/admin/favicon.svg",
  "solo-boss-cloud": "https://sbc-front.vercel.app/favicon.svg",
  "glot-it": "https://kensaur.us/glot-it/apple-touch-icon.png",
  glotit: "https://kensaur.us/glot-it/apple-touch-icon.png"
};
var UNTRUSTED_FAVICON_HOSTS = [
  "www.google.com",
  "icons.duckduckgo.com",
  "icon.horse"
];
function isUntrustedFaviconUrl(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return UNTRUSTED_FAVICON_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}
function faviconUrlCandidates(domain, _size = 32) {
  return [
    `https://${domain}/favicon.svg`,
    `https://${domain}/favicon.ico`,
    `https://${domain}/favicon-32x32.png`,
    `https://${domain}/apple-touch-icon.png`
  ];
}
function resolveProjectFaviconDomains(source) {
  const out = [];
  const add = (d) => {
    const n = d?.trim().toLowerCase();
    if (!n || out.includes(n)) return;
    out.push(n);
  };
  add(githubRepoDomainHint(source.repo_url));
  add(SLUG_DOMAIN_HINTS[source.project_slug.toLowerCase()]);
  const fromOrigin = source.sdk_origin ? originToDomain(source.sdk_origin) : null;
  if (fromOrigin && !MULTI_TENANT_PARENT_HOSTS.has(fromOrigin)) {
    add(fromOrigin);
  }
  return out;
}
function projectFaviconUrlCandidates(source, size = 32) {
  if (source.icon_url?.trim()) return [source.icon_url.trim()];
  const seen = /* @__PURE__ */ new Set();
  const urls = [];
  const push = (url) => {
    if (!url || seen.has(url) || isUntrustedFaviconUrl(url)) return;
    seen.add(url);
    urls.push(url);
  };
  const slugHint = SLUG_ICON_URL_HINTS[source.project_slug.toLowerCase()];
  if (slugHint) push(slugHint);
  for (const domain of resolveProjectFaviconDomains(source)) {
    for (const url of faviconUrlCandidates(domain, size)) {
      push(url);
    }
  }
  return urls;
}
function originToDomain(origin) {
  const trimmed = origin.trim();
  if (!trimmed) return null;
  if (/^(capacitor|file|content|app):/i.test(trimmed)) return null;
  try {
    const url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    const host = url.hostname.toLowerCase();
    if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
      return null;
    }
    return host;
  } catch {
    return null;
  }
}
function githubRepoDomainHint(repoUrl) {
  if (!repoUrl?.trim()) return null;
  try {
    const u = new URL(repoUrl.trim());
    const host = u.hostname.toLowerCase();
    const parts = u.pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
    if (parts.length < 2) return null;
    const repo = parts[1].replace(/\.git$/i, "").toLowerCase();
    if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(repo)) return repo;
    if (host === "github.com" && repo.endsWith(".github.io")) return repo;
    if (host !== "github.com" && host !== "www.github.com") return host;
    return null;
  } catch {
    return null;
  }
}
function resolveProjectDomain(source) {
  return resolveProjectFaviconDomains(source)[0] ?? null;
}
function isLikelyGenericFavicon(img) {
  if (typeof document === "undefined") return false;
  if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) return false;
  try {
    const canvas = document.createElement("canvas");
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0);
    const { data } = ctx.getImageData(0, 0, w, h);
    let opaque = 0;
    let satSum = 0;
    let maxDiff = 0;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a < 128) continue;
      opaque++;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const diff = max - min;
      maxDiff = Math.max(maxDiff, diff);
      satSum += diff;
    }
    if (opaque === 0) return true;
    const avgSat = satSum / opaque;
    return avgSat < 14 && maxDiff < 24;
  } catch {
    return isUntrustedFaviconUrl(img.src);
  }
}
function projectInitials(name) {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  const compact = name.replace(/[^a-zA-Z0-9]/g, "");
  return (compact.slice(0, 2) || name.slice(0, 2) || "?").toUpperCase();
}
var INITIALS_CHIP_THEMES = [
  "info",
  "brand",
  "warn",
  "ok",
  "accent"
];
function projectInitialsThemeIndex(projectId) {
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = (hash + projectId.charCodeAt(i)) % INITIALS_CHIP_THEMES.length;
  }
  return hash;
}
function readPageFaviconHref() {
  if (typeof document === "undefined") return null;
  const selectors = [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]'
  ];
  for (const sel of selectors) {
    const link = document.querySelector(sel);
    const href = link?.href;
    if (href && isSafeFaviconHref(href)) return href;
  }
  return null;
}
function isSafeFaviconHref(href) {
  try {
    const base = typeof location !== "undefined" ? location.href : void 0;
    const protocol = new URL(href, base).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

export { DEFAULT_API_ENDPOINT, DEFAULT_MAX_RETRIES, DEFAULT_TIMEOUT, MAX_REPORT_PAYLOAD_BYTES, MAX_SCREENSHOT_DATA_URL_BYTES, MUSHI_ACCENT_SHADOW, MUSHI_BANNER_BRAND_BORDER, MUSHI_BANNER_NEON, MUSHI_COLORS_DARK, MUSHI_COLORS_LIGHT, MUSHI_COPY, MUSHI_GEOMETRY, MUSHI_IDENTITY_TOKEN_PREFIX, MUSHI_INTERNAL_HEADER, MUSHI_INTERNAL_INIT_MARKER, MUSHI_INVERSE, MUSHI_MOTION, MUSHI_ON_ACCENT, MUSHI_PROJECT_HEADER, MUSHI_RADIUS, MUSHI_REPORTER_STATUS, MUSHI_SDK_PACKAGE_HEADER, MUSHI_SDK_VERSION_HEADER, MUSHI_SPACING, MUSHI_TIER_COLORS, MUSHI_TYPE, MUSHI_USER_TOKEN_HEADER, MUSHI_Z, REGION_ENDPOINTS, buildIdentityClaims, buildSdkIngestHeaders, captureEnvironment, checkReportPayloadSize, createApiClient, createBreadcrumbBuffer, createLogger, createOfflineQueue, createPiiScrubber, createPreFilter, createRateLimiter, estimateJsonBytes, faviconUrlCandidates, formatBytes, getDeviceFingerprintHash, getReporterToken, getSessionId, githubRepoDomainHint, hmacSha256Hex, isLikelyGenericFavicon, isUntrustedFaviconUrl, mushiPalette, mushiTokens, newUuid, noopLogger, normaliseThrown, originToDomain, parseIdentityToken, projectFaviconUrlCandidates, projectInitials, projectInitialsThemeIndex, readPageFaviconHref, resolveEnvConfig, resolveProjectDomain, resolveProjectFaviconDomains, resolveRegionEndpoint, resolveWidgetAccent, safeWidgetHex, scrubPii, sha256Hex };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map